import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { dashboardService } from '@/services';
import { CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { format, parseISO, startOfISOWeek } from 'date-fns';

interface DashboardAdHocUsageCardProps {
  companyId: string;
}

const LINE_COLORS = [
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
];

export function DashboardAdHocUsageCard({ companyId }: DashboardAdHocUsageCardProps) {
  const { data: items, isLoading } = useQuery({
    queryKey: ['dashboard-adhoc-usage', companyId],
    queryFn: async () => {
      const result = await dashboardService.getAdHocUsageByVendor(companyId);
      return result.data || [];
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!companyId,
  });

  const { chartData, vendorNames } = useMemo(() => {
    if (!items || items.length === 0) return { chartData: [], vendorNames: [] };

    // Count total items per vendor to find top 5
    const vendorCounts = new Map<string, number>();
    items.forEach((item) => {
      const name = item.vendor_name;
      vendorCounts.set(name, (vendorCounts.get(name) || 0) + 1);
    });

    const topVendors = Array.from(vendorCounts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name]) => name);

    // Group by ISO week + vendor (keyed by ISO string for correct sorting)
    const weekMap = new Map<string, Map<string, number>>();
    const weekLabels = new Map<string, string>();
    items.forEach((item) => {
      const weekStart = startOfISOWeek(parseISO(item.invoice_date));
      const weekKey = weekStart.toISOString();
      const vendor = item.vendor_name;

      if (!topVendors.includes(vendor)) return;

      weekLabels.set(weekKey, format(weekStart, 'MMM d'));
      if (!weekMap.has(weekKey)) {
        weekMap.set(weekKey, new Map());
      }
      const vendorMap = weekMap.get(weekKey)!;
      vendorMap.set(vendor, (vendorMap.get(vendor) || 0) + 1);
    });

    // Build chart data sorted by week (oldest first)
    const weekEntries = Array.from(weekMap.entries()).sort((a, b) =>
      a[0].localeCompare(b[0])
    );

    const data = weekEntries.map(([weekKey, vendorMap]) => {
      const point: Record<string, any> = { week: weekLabels.get(weekKey)! };
      topVendors.forEach((v) => {
        point[v] = vendorMap.get(v) || 0;
      });
      return point;
    });

    return { chartData: data, vendorNames: topVendors };
  }, [items]);

  return (
    <>
      <CardHeader className="pb-2">
        <CardTitle>Ad-Hoc Usage by Vendor</CardTitle>
      </CardHeader>
      <CardContent className="h-[calc(100%-60px)]">
        {isLoading ? (
          <Skeleton className="h-full w-full" />
        ) : chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis dataKey="week" fontSize={12} className="fill-muted-foreground" />
              <YAxis fontSize={12} className="fill-muted-foreground" allowDecimals={false} />
              <RechartsTooltip
                contentStyle={{
                  backgroundColor: 'hsl(var(--popover))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: 'var(--radius)',
                }}
              />
              <Legend />
              {vendorNames.map((vendor, idx) => (
                <Line
                  key={vendor}
                  type="monotone"
                  dataKey={vendor}
                  name={vendor}
                  stroke={LINE_COLORS[idx % LINE_COLORS.length]}
                  strokeWidth={2}
                  dot={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center text-muted-foreground">
            No ad-hoc usage data available
          </div>
        )}
      </CardContent>
    </>
  );
}
